import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Table,
  CardFooter,
} from "reactstrap";

import CustomerModal from "../../components/admin/customers/CustomerModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import InformationModal from "../../components/InformationModal";

import { ACTIONS, useCustomers } from "../../providers/customersProvider";

import { customersApi } from "../../services/customerServices";
import {
  faPen,
  faPlus,
  faSearch,
  faSync,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AdvanceTable from "../../components/advanceTable/AdvanceTable";
import AdvanceTablePagination from "../../components/advanceTable/AdvanceTablePagination";
import AdvanceTableWrapper from "../../components/advanceTable/AdvanceTableWrapper";

const initConfirmationModal = {
  isOpen: false,
  onSubmit: null,
  onClose: null,
  title: "",
  body: "",
};

const Customers = () => {
  const [customersContext, setCustomersContext] = useCustomers();

  const [loading, setLoading] = useState(true);

  const [confirmationModal, setConfirmationModal] = useState(
    initConfirmationModal
  );

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  const [createCustomerModal, setCreateCustomerModal] = useState(false);

  const [editCustomerModal, setEditCustomerModal] = useState(false);

  const columns = [
    {
      accessor: "name",
      Header: "Name",
      headerProps: { className: "text-truncate" },
      Cell: (rowData) => {
        const { name } = rowData.row.original;
        return name || "-";
      },
    },
    {
      accessor: "id",
      Header: "",
      disableSortBy: true,
      headerProps: { className: "text-truncate text-right" },
      cellProps: { className: "text-truncate text-right" },
      Cell: (rowData) => (
        <div>
          <Button
            size="sm"
            className="rounded"
            color="warning"
            onClick={() => onEdit(rowData.row.original)}
          >
            <span>Edit</span>
            <FontAwesomeIcon icon={faPen} className="ml-2" />
          </Button>
          <Button
            size="sm"
            className="rounded ml-2"
            color="danger"
            onClick={() => onDelete(rowData.row.original)}
          >
            <span>Delete</span>
            <FontAwesomeIcon icon={faTrash} className="ml-2" />
          </Button>
          <Button
            size="sm"
            className="rounded ml-2"
            color="info"
            onClick={() => onSeeContacts(rowData.row.original.contacts)}
          >
            <span>See Contacts</span>
          </Button>
        </div>
      ),
    },
  ];

  const onSort = useCallback(
    ([data]) => {
      if (data) {
        const sortBy = data.id;
        const direction = data.desc ? "desc" : "asc";
        if (
          customersContext.sortBy === sortBy.id &&
          customersContext.direction === direction
        ) {
          return;
        }
        setCustomersContext({
          action: ACTIONS.SORT,
          payload: { sortBy, direction },
        });
      } else {
        setCustomersContext({
          action: ACTIONS.SORT,
          payload: { sortBy: null, direction: null },
        });
      }
    },
    [setCustomersContext, customersContext.direction, customersContext.sortBy]
  );

  const onEdit = (customer) => {
    setEditCustomerModal(customer.id);
  };

  const onDelete = (customer) => {
    setConfirmationModal({
      isOpen: true,
      onSubmit: async () => {
        try {
          await customersApi.deleteCustomer(customer);
          setConfirmationModal(initConfirmationModal);
          setCustomersContext({
            action: ACTIONS.REFRESH,
          });
        } catch (err) {}
      },
      onClose: () => {
        setConfirmationModal(initConfirmationModal);
        setCustomersContext({
          action: ACTIONS.REFRESH,
        });
      },
      title: "Delete Customer",
      body: `<p class="text-center">Do you confirm you want to delete this customer?</p>
        <span>${customer.name}</span>`,
      confirmColor: "danger",
    });
  };

  const onSeeContacts = (contacts) =>
    setInformationModal({
      isOpen: true,
      rawBody: true,
      title: `Contacts`,
      body: (
        <div className="d-flex">
          <Table className="col-12 px-0 mb-0 border" stripped="true">
            <thead className="small">
              <tr className="bg-lighter">
                <th>First Name</th>
                <th>Last Name</th>
                <th>Title</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {contacts
                .sort((a, b) =>
                  a.firstName < b.firstName
                    ? -1
                    : b.firstName < a.firstName
                    ? 1
                    : 0
                )
                .map(({ id, firstName, lastName, title, email }) => (
                  <tr key={id}>
                    <td>{firstName}</td>
                    <td>{lastName}</td>
                    <td>{title}</td>
                    <td>{email}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      ),
    });

  useEffect(() => {
    setLoading(true);
    customersApi
      .getCustomers({
        search: customersContext.search,
        page: customersContext.page - 1,
        pageSize: customersContext.sizePerPage,
        sortBy: customersContext.sortBy,
        direction: customersContext.direction,
      })
      .then((customers) => {
        setLoading(false);
        setCustomersContext({
          action: ACTIONS.GET_CUSTOMERS_SUCCESS,
          payload: { customers },
        });
      })
      .catch(() => {
        setLoading(false);
        return setInformationModal({
          isOpen: true,
          title: "Error",
          body: "There was an error with your request.",
        });
      });
  }, [
    setCustomersContext,
    customersContext.sortBy,
    customersContext.direction,
    customersContext.sizePerPage,
    customersContext.page,
    customersContext.search,
    customersContext.refresh,
  ]);

  return (
    <Container fluid className="h-100">
      <div className="w-100">
        <AdvanceTableWrapper
          columns={columns}
          data={customersContext.customers?.data || []}
          pageSize={customersContext.sizePerPage}
          sortable
          onSort={onSort}
          defaultSort={{
            sortBy: customersContext.sortBy,
            direction: customersContext.direction,
          }}
        >
          <Card className="mb-3 w-100 overflow-x-auto">
            <CardHeader className="d-flex">
              <div className="text-dark flex-grow-1 d-flex align-items-center">
                <h3 className="mb-0 ">Customers</h3>
                <small className="text-muted ml-2 pt-1">
                  ({customersContext.customers?.count})
                </small>
              </div>
              <div className=" d-flex align-items-center justify-content-between">
                <InputGroup size="m" className="mr-3">
                  <Input
                    maxLength="50"
                    placeholder="Search for.."
                    value={customersContext.search}
                    onChange={(evt) =>
                      setCustomersContext({
                        action: ACTIONS.SEARCH,
                        payload: { search: evt.target.value },
                      })
                    }
                  />
                  <InputGroupText className="search-jobs input-group-text bg-primary text-white border-left-0 border-primary">
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroupText>
                </InputGroup>
                <Button
                  size="sm"
                  className="mr-3 rounded-circle custom-rounded-button d-flex "
                  color="primary"
                  onClick={() => setCreateCustomerModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
                <Button
                  size="sm"
                  className="rounded-circle custom-rounded-button d-flex "
                  color="primary"
                  onClick={() =>
                    setCustomersContext({
                      action: ACTIONS.REFRESH,
                    })
                  }
                >
                  <FontAwesomeIcon icon={faSync} size="sm" />
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <AdvanceTable
                table
                isLoading={loading}
                headerClassName="text-muted small"
                tableProps={{
                  striped: true,
                  className: "mb-0",
                }}
              />
            </CardBody>
            <CardFooter>
              <AdvanceTablePagination
                totalCount={customersContext.customers?.count}
                pageCount={customersContext.customers?.totalPages}
                currentPage={customersContext.page - 1}
                onPageChange={(page) =>
                  setCustomersContext({
                    action: ACTIONS.PAGE_CHANGE,
                    payload: { page },
                  })
                }
                pageSize={customersContext.sizePerPage}
                onPageSizeChange={(sizePerPage) =>
                  setCustomersContext({
                    action: ACTIONS.PAGE_SIZE_CHANGE,
                    payload: { sizePerPage },
                  })
                }
              />
            </CardFooter>
          </Card>
        </AdvanceTableWrapper>
      </div>
      {createCustomerModal && (
        <CustomerModal
          key="create"
          onClose={() => {
            setCreateCustomerModal(false);
          }}
          onSubmit={() => {
            setCreateCustomerModal(false);
            setInformationModal({
              isOpen: true,
              title: "Create Customer",
              body: "Customer created successfully.",
            });
            setCustomersContext({
              action: ACTIONS.REFRESH,
            });
          }}
        />
      )}
      {editCustomerModal && (
        <CustomerModal
          key={`edit-${editCustomerModal}`}
          customerId={editCustomerModal}
          onClose={() => {
            setEditCustomerModal(false);
          }}
          onSubmit={() => {
            setEditCustomerModal(false);
            setInformationModal({
              isOpen: true,
              title: "Update Customer",
              body: "Customer Updated Successfully.",
            });
            setCustomersContext({
              action: ACTIONS.REFRESH,
            });
          }}
        />
      )}
      {confirmationModal.isOpen ? (
        <ConfirmationModal {...confirmationModal} />
      ) : null}
      {informationModal.isOpen ? (
        <InformationModal
          {...informationModal}
          onClose={() => setInformationModal({ isOpen: false })}
        />
      ) : null}
    </Container>
  );
};

export default Customers;
